import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import dayjs from 'dayjs';

import { LinkButton } from '@components/Button';
import { PATIENTS } from '@constants/routes';
import { Clinician } from '@customTypes/clinician';
import { ResultsType } from '@customTypes/order';
import { ExtendedRequisitionListResultItem } from '@customTypes/requisition';
import { IUsername } from '@customTypes/user';
import { getClinicianName } from '@helpers/clinician';
import { getIconByResult } from '@helpers/order';
import { getRequisitionResult, hasRequisitionDownloads } from '@helpers/requisition';
import { Breakpoint, SIZE_SM, SIZE_XS } from '@hooks/useBreakpoint';
import { RequisitionDownloadMenu } from '../RequisitionDownloadMenu/RequisitionDownloadMenu';
import { ResultFilter } from './ResultFilter';
import { StatusFilter } from './StatusFilter';
import { RequisitionIdFloatingFilter } from './RequisitionIdFloatingFilter';
import { PatientNameFloatingFilter } from './PatientNameFloatingFilter';

type RequisitionCellRendererProps = ICellRendererParams<ExtendedRequisitionListResultItem>;

export interface RequisitionColumnDefinitions {
  clinician: ColDef<ExtendedRequisitionListResultItem, Clinician | null>;
  download: ColDef<ExtendedRequisitionListResultItem, void>;
  externalMrn: ColDef<ExtendedRequisitionListResultItem, string | null>;
  orderDate: ColDef<ExtendedRequisitionListResultItem, string>;
  patient: ColDef<ExtendedRequisitionListResultItem, IUsername>;
  requisitionId: ColDef<ExtendedRequisitionListResultItem, string>;
  result: ColDef<ExtendedRequisitionListResultItem, ResultsType>;
  sampleReceivedAt: ColDef<ExtendedRequisitionListResultItem, string | null>;
  status: ColDef<ExtendedRequisitionListResultItem, string | null>;
}

export const defaultColumnDef: Readonly<ColDef<ExtendedRequisitionListResultItem>> = {
  filter: false,
  floatingFilter: true,
  sortable: false,
  suppressFloatingFilterButton: true,
  suppressHeaderMenuButton: true,
};

interface RequisitionTableColumnsState {
  breakpoint: Breakpoint;
  showPatientName: boolean;
}

/**
 * Values of the defined columns `colId` property
 */
export type RequisitionTableColumnId =
  | 'download'
  | 'patient'
  | 'sample_received_at'
  | 'requisition_id'
  | 'status'
  | 'result'
  | 'order_date'
  | 'clinician'
  | 'external_mrn';

// Some columns are currently configured so the floating filter row has the filter toggle,
// which opens the filter modal, but does not show the filter input
// There is no way to do this without creating an entire component that replaces both, so we
// render nothing into the input section, which is placed within `.ag-floating-filter-body`
const NullFloatingFilterComponent = () => null;

export const getRequisitionTableColumns = (state: RequisitionTableColumnsState): RequisitionColumnDefinitions => {
  const download: ColDef<ExtendedRequisitionListResultItem, void> = {
    colId: 'download',
    // Results
    field: undefined,
    filter: false,
    sortable: false,
    // Header
    headerName: '',
    headerClass: 'px-0',
    suppressHeaderFilterButton: true,
    // Position
    pinned: 'left',
    resizable: false,
    suppressAutoSize: true,
    suppressMovable: true,
    suppressSizeToFit: true,
    width: 34,
    // Cell
    cellClass: 'px-0',
    cellRenderer: (cellProps: RequisitionCellRendererProps) => {
      return cellProps.data ? <RequisitionDownloadMenu requisition={cellProps.data} /> : null;
    },
    tooltipValueGetter: params => {
      const rowRequisition = params.data;

      return rowRequisition && hasRequisitionDownloads(rowRequisition) ? '' : 'No downloads available';
    },
  };

  const patient: ColDef<ExtendedRequisitionListResultItem, IUsername> = {
    colId: 'patient',
    // Results
    field: 'name',
    filter: 'agTextColumnFilter',
    floatingFilterComponent: PatientNameFloatingFilter,
    filterParams: {
      debounceMs: 500,
    },
    sortable: true,
    // Position
    initialWidth: state.breakpoint === SIZE_XS ? 140 : state.breakpoint === SIZE_SM ? 180 : 220,
    pinned: 'left',
    suppressMovable: true,
    // Header
    headerName: 'Patient',
    // Cell
    cellClassRules: {
      'blurred-cell': () => !state.showPatientName,
    },
    cellRenderer: (cellProps: RequisitionCellRendererProps) => {
      const rowRequisition = cellProps.data;

      if (!rowRequisition) {
        return null;
      }

      return (
        <LinkButton
          size='sm'
          to={`${PATIENTS}/${rowRequisition.individual_id}`}
          variant='link'
          data-openreplay-sanitize
        >
          {cellProps.valueFormatted}
        </LinkButton>
      );
    },
    valueFormatter: params => {
      if (!params.value) {
        return '';
      }

      const { first, last } = params.value;

      return state.showPatientName ? `${first} ${last}` : '#'.repeat(first.length + last.length);
    },
  };

  const sampleReceivedAt: ColDef<ExtendedRequisitionListResultItem, string | null> = {
    colId: 'sample_received_at',
    // Results
    field: 'sample_received_at',
    filter: 'agDateColumnFilter',
    sortable: true,
    // Header
    headerName: 'Sample Received',
    // Cell
    valueFormatter: params => (params.value ? dayjs.utc(params.value).format('MM/DD/YYYY') : ''),
  };

  const requisitionId: ColDef<ExtendedRequisitionListResultItem, string> = {
    colId: 'requisition_id',
    // Results
    field: 'requisition_id',
    filter: 'agTextColumnFilter',
    floatingFilterComponent: RequisitionIdFloatingFilter,
    filterParams: {
      debounceMs: 10000,
    },
    sortable: true,
    // Header
    headerName: 'Order ID',
    // Cell
    cellRenderer: (cellProps: RequisitionCellRendererProps) => {
      const rowRequisition = cellProps.data;

      if (!rowRequisition) {
        return null;
      }

      return (
        <LinkButton size='sm' to={`${PATIENTS}/${rowRequisition.individual_id}`} variant='link'>
          {rowRequisition.requisition_id}
        </LinkButton>
      );
    },
  };

  const status: ColDef<ExtendedRequisitionListResultItem, string | null> = {
    colId: 'status',
    // Results
    field: 'display_status',
    filter: StatusFilter,
    floatingFilterComponent: NullFloatingFilterComponent,
    suppressFloatingFilterButton: false,
    sortable: false,
    // Position
    initialWidth: 150,
    // Header
    headerName: 'Status',
  };

  const result: ColDef<ExtendedRequisitionListResultItem, ResultsType> = {
    colId: 'result',
    // Results
    field: undefined,
    filter: ResultFilter,
    floatingFilterComponent: NullFloatingFilterComponent,
    suppressFloatingFilterButton: false,
    sortable: false,
    // Position
    initialWidth: 90,
    // Header
    headerName: 'Results',
    // Cell
    cellClass: 'ag-cell-center',
    valueGetter: params => getRequisitionResult(params.data),
    cellRenderer: (cellProps: RequisitionCellRendererProps) => {
      const result = cellProps.value;
      const icon = getIconByResult(result);

      if (!icon) {
        return <div aria-label={result} />;
      }

      return <FontAwesomeIcon aria-label={result} color='primary text-center' fontSize={16} icon={icon as IconProp} />;
    },
  };

  const orderDate: ColDef<ExtendedRequisitionListResultItem, string> = {
    colId: 'order_date',
    // Results
    field: 'order_date',
    filter: 'agDateColumnFilter',
    initialSort: 'desc',
    initialSortIndex: 0,
    sortable: true,
    // Header
    headerName: 'Order Date',
    // Cell
    valueFormatter: params => (params.value ? dayjs.utc(params.value).format('MM/DD/YYYY') : ''),
  };

  const clinician: ColDef<ExtendedRequisitionListResultItem, Clinician | null> = {
    colId: 'clinician',
    // Results
    field: 'ordering_provider',
    filter: 'agTextColumnFilter',
    sortable: true,
    // Header
    headerName: 'Clinician',
    // Cell
    valueFormatter: params => {
      return params.value ? getClinicianName(params.value) : '';
    },
  };

  const externalMrn: ColDef<ExtendedRequisitionListResultItem, string | null> = {
    colId: 'external_mrn',
    field: 'external_mrn',
    filter: 'agTextColumnFilter',
    sortable: true,
    // Header
    headerName: 'External ID',
  };

  return {
    clinician,
    download,
    externalMrn,
    orderDate,
    patient,
    requisitionId,
    result,
    sampleReceivedAt,
    status,
  };
};
